import React, { useState, useEffect, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import TaskCategorizationPanel from '@/components/gamification/TaskCategorizationPanel';
import { format, startOfDay } from 'date-fns';
import {
  Plus, X, Sparkles, AlertTriangle, AlertOctagon, Bot, Upload, Loader2
} from 'lucide-react';

const EMPTY_FORM = {
  activity_id: '',
  task_description: '',
  key_result_focus: '',
  time_allocation_percent: 40,
  task_order: 1,
  status_flag: 'gray',
  tags: [],
  assign_to_email: '',
  is_tentative: false,
  category: null,
  xp_value: 0,
  ep_value: 0,
  skill_rewards: [],
  bot_suggested_category: null,
  bot_suggested_xp: null,
  bot_suggested_ep: null,
  bot_suggested_skills: null,
  bot_suggestion_approved: false,
};

/**
 * Shared Add/Edit Task Dialog — identical to the form in DailyTaskPlanner.
 * 
 * Props:
 *  - open, onClose
 *  - onSubmit(taskData)    — receives the fully assembled task data object
 *  - user                  — current auth user
 *  - officers              — OfficerProfile list
 *  - allActivities         — Activity list
 *  - missions              — Mission list
 *  - objectives            — Objective list
 *  - editingTask?          — if set, dialog is in edit mode
 *  - initialDate?          — pre-set date string 'yyyy-MM-dd' (from DailyTasks column click)
 *  - kanbanStage?          — pre-set kanban stage (from TaskBoard column click)
 *  - dateRequired?         — whether date is required (true in DailyTasks, false in TaskBoard backlog)
 *  - showDatePicker?       — whether to show the date input field (true in TaskBoard, false in DailyTasks since date is contextual)
 *  - titleOverride?        — optional custom dialog title
 */
export default function AddEditTaskDialog({
  open,
  onClose,
  onSubmit,
  user,
  officers = [],
  allActivities = [],
  missions = [],
  objectives = [],
  editingTask = null,
  initialDate = null,
  kanbanStage = null,
  dateRequired = true,
  showDatePicker = false,
  titleOverride = null,
}) {
  const queryClient = useQueryClient();

  // ─── Internal state ───────────────────────────────────────────────────────

  const [taskForm, setTaskForm] = useState({ ...EMPTY_FORM });
  const [currentTagType, setCurrentTagType] = useState('');
  const [currentTagValue, setCurrentTagValue] = useState('');
  const [dialogMissionFilter, setDialogMissionFilter] = useState([]);
  const [dialogObjectiveFilter, setDialogObjectiveFilter] = useState([]);
  const [isGeneratingTask, setIsGeneratingTask] = useState(false);
  const [aiAssessment, setAiAssessment] = useState(null);
  const [uploadingArtifact, setUploadingArtifact] = useState(false);
  const [dateValue, setDateValue] = useState('');

  // ─── Data for tags ────────────────────────────────────────────────────────

  const { data: artifacts = [] } = useQuery({
    queryKey: ['artifacts'],
    queryFn: () => base44.entities.Artifact.list(),
    initialData: [],
  });

  const { data: rituals = [] } = useQuery({
    queryKey: ['rituals'],
    queryFn: () => base44.entities.Ritual.list(),
    initialData: [],
  });

  const { data: policies = [] } = useQuery({
    queryKey: ['policies'],
    queryFn: () => base44.entities.Policy.list(),
    initialData: [],
  });

  const { data: capabilities = [] } = useQuery({
    queryKey: ['capabilities'],
    queryFn: () => base44.entities.Capability.list(),
    initialData: [],
  });

  // ─── Initialize form when dialog opens ────────────────────────────────────

  useEffect(() => {
    if (!open) return;

    if (editingTask) {
      setTaskForm({
        activity_id: editingTask.activity_id || '',
        task_description: editingTask.task_description || '',
        key_result_focus: editingTask.key_result_focus || '',
        time_allocation_percent: editingTask.time_allocation_percent ?? 40,
        task_order: editingTask.task_order ?? 1,
        status_flag: editingTask.status_flag || 'gray',
        tags: editingTask.tags || [],
        assign_to_email: editingTask.user_email !== user?.email ? editingTask.user_email : '',
        is_tentative: editingTask.status === 'tentative' || editingTask.status === 'rejected',
        category: editingTask.category || null,
        xp_value: editingTask.xp_value || 0,
        ep_value: editingTask.ep_value || 0,
        skill_rewards: editingTask.skill_rewards || [],
        bot_suggested_category: editingTask.bot_suggested_category || null,
        bot_suggested_xp: editingTask.bot_suggested_xp || null,
        bot_suggested_ep: editingTask.bot_suggested_ep || null,
        bot_suggested_skills: editingTask.bot_suggested_skills || null,
        bot_suggestion_approved: editingTask.bot_suggestion_approved || false,
      });
      setDateValue(editingTask.date || '');
      // Set dialog filters based on editing task's activity
      if (editingTask.activity_id) {
        const activity = allActivities.find(a => a.id === editingTask.activity_id);
        if (activity) {
          const objective = objectives.find(o => o.id === activity.objective_id);
          setDialogMissionFilter(objective?.mission_id ? [objective.mission_id] : []);
          setDialogObjectiveFilter(objective?.id ? [objective.id] : []);
        }
      }
    } else {
      setTaskForm({
        ...EMPTY_FORM,
        status_flag: kanbanStage === 'in_progress' ? 'yellow' : 'gray',
      });
      setDateValue(initialDate || (kanbanStage === 'backlog' ? '' : format(new Date(), 'yyyy-MM-dd')));
      setDialogMissionFilter([]);
      setDialogObjectiveFilter([]);
    }

    setCurrentTagType('');
    setCurrentTagValue('');
    setAiAssessment(null);
  }, [open, editingTask, initialDate, kanbanStage]);

  // ─── Derived ──────────────────────────────────────────────────────────────

  const selectedActivity = allActivities.find(a => a.id === taskForm.activity_id);

  // ─── Tag helpers ──────────────────────────────────────────────────────────

  const addTag = () => {
    if (currentTagType && currentTagValue.trim()) {
      const tagString = `${currentTagValue.trim()} (${currentTagType})`;
      if (!taskForm.tags.includes(tagString)) {
        setTaskForm({ ...taskForm, tags: [...taskForm.tags, tagString] });
        setCurrentTagType('');
        setCurrentTagValue('');
      }
    }
  };

  const removeTag = (tagToRemove) => {
    setTaskForm({ ...taskForm, tags: taskForm.tags.filter(tag => tag !== tagToRemove) });
  };

  // ─── Artifact upload (edit mode) ──────────────────────────────────────────

  const handleArtifactUpload = async (e, task) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingArtifact(true);
    try {
      const uploadResponse = await base44.functions.invoke('uploadToGoogleDrive', {
        file: file,
        context_type: 'Task',
        parent_context_id: task.id,
      });
      if (!uploadResponse.data.success) throw new Error(uploadResponse.data.error || 'Upload failed');
      const doc = uploadResponse.data.document;
      await base44.entities.Artifact.create({
        name: doc.name,
        description: `Artifact created from task: ${task.task_description.substring(0, 50)}...`,
        category: 'other',
        type: 'uploaded',
        file_url: doc.drive_web_link,
        owner_email: user?.email,
        status: 'pending_approval',
        linked_task_id: task.id,
        tags: task.tags || [],
      });
      alert('Artifact uploaded successfully and sent for approval!');
      queryClient.invalidateQueries({ queryKey: ['artifacts'] });
    } catch (error) {
      console.error('Artifact upload error:', error);
      alert(`Failed to upload artifact: ${error.message}`);
    } finally {
      setUploadingArtifact(false);
    }
  };

  // ─── Reset form ───────────────────────────────────────────────────────────

  const resetForm = () => {
    setTaskForm({ ...EMPTY_FORM });
    setCurrentTagType('');
    setCurrentTagValue('');
    setAiAssessment(null);
    setDialogMissionFilter([]);
    setDialogObjectiveFilter([]);
    setDateValue('');
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    if (!taskForm.task_description) {
      alert('Task description is required');
      return;
    }
    if (!taskForm.activity_id) {
      alert('Activity is required');
      return;
    }

    const targetUserEmail = taskForm.assign_to_email || user.email;
    const targetOfficer = officers.find(o => o.user_email === targetUserEmail);
    const isAIOfficer = targetOfficer?.custom_role === 'AI Officer';

    // Time allocation validation (skip for AI officers)
    if (!isAIOfficer) {
      const minMax = {
        1: { min: 40, max: 100 },
        2: { min: 30, max: 60 },
        3: { min: 0, max: 100 },
      };
      if (taskForm.task_order <= 3) {
        const taskOrderValidation = minMax[taskForm.task_order];
        if (taskOrderValidation) {
          const { min, max } = taskOrderValidation;
          if (taskForm.time_allocation_percent < min || taskForm.time_allocation_percent > max) {
            alert(`Task ${taskForm.task_order} must be between ${min}% and ${max}% of your time`);
            return;
          }
        }
      }
    } else {
      taskForm.time_allocation_percent = 0;
    }

    const isCreatingForSelf = !taskForm.assign_to_email || taskForm.assign_to_email === user.email;
    const isAssigningToBot = isAIOfficer;

    // Build task data
    const taskData = {
      ...taskForm,
      user_email: targetUserEmail,
      date: dateValue || null,
      status: editingTask
        ? (taskForm.is_tentative ? 'tentative' : (editingTask.status === 'rejected' ? 'tentative' : editingTask.status))
        : ((isCreatingForSelf || isAssigningToBot) ? 'approved' : 'tentative'),
      generated_by_ai: editingTask?.generated_by_ai || false,
      category: taskForm.category,
      xp_value: taskForm.xp_value || 0,
      ep_value: taskForm.ep_value || 0,
      skill_rewards: taskForm.skill_rewards || [],
      bot_suggested_category: taskForm.bot_suggested_category,
      bot_suggested_xp: taskForm.bot_suggested_xp,
      bot_suggested_ep: taskForm.bot_suggested_ep,
      bot_suggested_skills: taskForm.bot_suggested_skills,
      bot_suggestion_approved: taskForm.bot_suggestion_approved || false,
    };

    // Add kanban_stage if provided
    if (kanbanStage) {
      taskData.kanban_stage = kanbanStage;
    }

    // Add created_by_email for tasks assigned to others
    if (!isCreatingForSelf && !isAssigningToBot) {
      taskData.created_by_email = user.email;
    }

    // Clean up internal-only fields
    delete taskData.assign_to_email;
    delete taskData.is_tentative;

    onSubmit(taskData, editingTask);
    resetForm();
    onClose();
  };

  // ─── Dialog title ─────────────────────────────────────────────────────────

  const dialogTitle = titleOverride
    ? titleOverride
    : editingTask
      ? 'Edit Task'
      : initialDate
        ? `Add Task for ${format(new Date(initialDate + 'T00:00:00'), 'MMM d, yyyy')}`
        : 'Add Task';

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <ConfirmableDialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        resetForm();
        onClose();
      }
    }}>
      <DialogContent data-testid="tasks-addedittaskdialog-dialog" className="bg-slate-900 text-white border-slate-800 max-w-2xl max-h-[95vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-16px)] sm:w-full p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 min-w-0">

          {/* ── Task Order & Time Allocation ── */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Task Order & Time Allocation</label>
            {(() => {
              const targetOfficer = officers.find(o => o.user_email === (taskForm.assign_to_email || user?.email));
              const isAI = targetOfficer?.custom_role === 'AI Officer';
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={taskForm.task_order.toString()}
                    onValueChange={(value) => setTaskForm({ ...taskForm, task_order: parseInt(value) })}
                  >
                    <SelectTrigger className="w-32 bg-slate-950 border-slate-700 text-slate-100">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Task #1</SelectItem>
                      <SelectItem value="2">Task #2</SelectItem>
                      <SelectItem value="3">Task #3</SelectItem>
                      <SelectItem value="4">Task #4 (Additional)</SelectItem>
                      <SelectItem value="5">Task #5 (Additional)</SelectItem>
                      <SelectItem value="6">Task #6 (Additional)</SelectItem>
                      <SelectItem value="7">Task #7 (Additional)</SelectItem>
                      <SelectItem value="8">Task #8 (Additional)</SelectItem>
                      <SelectItem value="9">Task #9 (Additional)</SelectItem>
                      <SelectItem value="10">Task #10 (Additional)</SelectItem>
                    </SelectContent>
                  </Select>
                  {!isAI && (
                    <>
                      <Input data-testid="tasks-addedittaskdialog-input"
                        type="number"
                        value={taskForm.time_allocation_percent}
                        onChange={(e) => setTaskForm({ ...taskForm, time_allocation_percent: parseInt(e.target.value) })}
                        className="w-24 bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                        min={taskForm.task_order === 1 ? 40 : taskForm.task_order === 2 ? 30 : 0}
                        max={taskForm.task_order === 1 ? 100 : taskForm.task_order === 2 ? 60 : 100}
                      />
                      <span className="text-sm text-slate-400">
                        % of day ({taskForm.task_order === 1 ? '40-100%' : taskForm.task_order === 2 ? '30-60%' : taskForm.task_order === 3 ? 'remainder' : 'flexible'})
                      </span>
                    </>
                  )}
                  {isAI && (
                    <span className="text-sm text-purple-400">
                      Time allocation doesn't apply to AI Officers - only task order matters
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* ── Date Picker (when showDatePicker is true, e.g. TaskBoard) ── */}
          {showDatePicker && (
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                Date {!dateRequired ? '(optional for backlog)' : '*'}
              </label>
              <Input data-testid="tasks-addedittaskdialog-input-1"
                type="date"
                value={dateValue}
                onChange={(e) => setDateValue(e.target.value)}
                className="bg-slate-950 border-slate-700 text-slate-100 w-48"
              />
            </div>
          )}

          {/* ── Assign To (for commanders / squad assignment) ── */}
          <div className="min-w-0">
            <label className="text-sm text-slate-400 mb-2 block">Assign To</label>
            <SearchableTagSelect
              options={[
                { value: '', label: `Self (${user?.email?.split('@')[0] || 'me'})`, isMy: true },
                ...officers
                  .filter(o => o.user_email !== user?.email)
                  .map(o => ({
                    value: o.user_email,
                    label: `${o.callsign} (${o.rank_title})${o.custom_role === 'AI Officer' ? ' 🤖' : ''}`,
                    isMy: false,
                    isAI: o.custom_role === 'AI Officer',
                  }))
              ]}
              value={taskForm.assign_to_email}
              onValueChange={(v) => setTaskForm({ ...taskForm, assign_to_email: v })}
              placeholder="Self"
              emptyMessage="No officers found"
              className="bg-slate-950 border-slate-700 text-slate-100 max-w-full"
              renderItem={(option) => (
                <span className="flex items-center">
                  {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                  {option.isAI && <span className="text-purple-400 mr-1">🤖</span>}
                  {option.label}
                </span>
              )}
            />
          </div>

          {/* ── Filter by Mission ── */}
          <div className="min-w-0">
            <label className="text-sm text-slate-400 mb-2 block">Filter by Mission</label>
            <SearchableTagSelect
              options={missions.map(m => ({
                value: m.id,
                label: `${m.mission_code ? `[${m.mission_code}] ` : ''}${m.title}`,
                isMy: m.owner_email === user?.email || m.assigned_to_email === user?.email,
              }))}
              value={dialogMissionFilter[0] || ''}
              onValueChange={(value) => setDialogMissionFilter([value])}
              placeholder="All missions"
              emptyMessage="No missions found"
              className="bg-slate-950 border-slate-700 text-slate-100 max-w-full"
              renderItem={(option) => (
                <span className="flex items-center">
                  {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                  {option.label}
                </span>
              )}
            />
          </div>

          {/* ── Filter by Objective ── */}
          <div className="min-w-0">
            <label className="text-sm text-slate-400 mb-2 block">Filter by Objective</label>
            <SearchableTagSelect
              options={objectives
                .filter(o => dialogMissionFilter.length === 0 || dialogMissionFilter.includes(o.mission_id))
                .map(o => ({
                  value: o.id,
                  label: `${o.objective_code ? `[${o.objective_code}] ` : ''}${o.statement}`,
                  isMy: o.owner_email === user?.email,
                }))}
              value={dialogObjectiveFilter[0] || ''}
              onValueChange={(value) => setDialogObjectiveFilter([value])}
              placeholder="All objectives"
              emptyMessage="No objectives found"
              className="bg-slate-950 border-slate-700 text-slate-100 max-w-full"
              renderItem={(option) => (
                <span className="flex items-center">
                  {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                  {option.label}
                </span>
              )}
            />
          </div>

          {/* ── Link to Activity ── */}
          <div className="min-w-0">
            <label className="text-sm text-slate-400 mb-2 block">Link to Activity *</label>
            <SearchableTagSelect
              options={allActivities
                .filter(a => {
                  if (!(a.status === 'active' || (editingTask && a.id === editingTask.activity_id)) || !a.id) return false;
                  if (dialogMissionFilter.length > 0) {
                    const objective = objectives.find(o => o.id === a.objective_id);
                    if (!objective || !dialogMissionFilter.includes(objective.mission_id)) return false;
                  }
                  if (dialogObjectiveFilter.length > 0) {
                    if (!dialogObjectiveFilter.includes(a.objective_id)) return false;
                  }
                  const targetOfficer2 = taskForm.assign_to_email || user?.email;
                  const botOfficer = officers.find(o => o.user_email === targetOfficer2);
                  if (botOfficer?.is_bot) {
                    if (botOfficer.bot_owner_email !== user?.email) return false;
                  }
                  return true;
                })
                .map(activity => {
                  const responsibleOfficer = officers.find(o => o.user_email === activity.responsible_email);
                  const isAI = responsibleOfficer?.custom_role === 'AI Officer';
                  const isMyActivity = activity.accountable_email === user?.email || activity.responsible_email === user?.email;
                  return {
                    value: activity.id,
                    label: `${activity.status === 'archived' ? '[archived!] ' : ''}[${activity.activity_code}] ${activity.title}`,
                    isMy: isMyActivity,
                    isAI: isAI,
                    isArchived: activity.status === 'archived',
                  };
                })}
              value={taskForm.activity_id}
              renderItem={(option) => (
                <span className="flex items-center">
                  {option.isMy && <span className="text-amber-400 mr-1">⭐</span>}
                  {option.isAI && <span className="text-purple-400 mr-1">🤖</span>}
                  {option.label}
                </span>
              )}
              onValueChange={(v) => {
                const act = allActivities.find(a => a.id === v);
                const respOfficer = officers.find(o => o.user_email === act?.responsible_email);
                const isAIResponsible = respOfficer?.custom_role === 'AI Officer';

                // Preserve pre-set assignment
                if (taskForm.assign_to_email) {
                  setTaskForm({ ...taskForm, activity_id: v });
                  return;
                }

                const isResponsibleOrAccountable = act?.responsible_email === user?.email || act?.accountable_email === user?.email;
                let newAssignToEmail = '';
                let newIsTentative = false;

                if (!isResponsibleOrAccountable) {
                  newIsTentative = true;
                } else if (isAIResponsible && act?.accountable_email === user?.email) {
                  newAssignToEmail = act.responsible_email;
                }

                setTaskForm({ ...taskForm, activity_id: v, assign_to_email: newAssignToEmail, is_tentative: newIsTentative });
              }}
              placeholder="Select an activity..."
              emptyMessage="No activities found"
              className="bg-slate-950 border-slate-700 text-slate-100 max-w-full"
            />
          </div>

          {/* ── Tentative / AI Officer info banners ── */}
          {taskForm.is_tentative && !taskForm.assign_to_email && (
            <div className="p-3 rounded-lg border bg-amber-500/10 border-amber-500/30">
              <div className="flex items-center gap-2 text-sm text-amber-300">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  Creating tentative task (requires approval from {(() => {
                    const activity = allActivities.find(a => a.id === taskForm.activity_id);
                    const approverEmail = activity?.accountable_email || activity?.responsible_email;
                    const approver = officers.find(o => o.user_email === approverEmail);
                    return approver?.callsign || approverEmail || 'activity owner';
                  })()})
                </span>
              </div>
            </div>
          )}
          {taskForm.assign_to_email && !taskForm.is_tentative && (
            <div className="p-3 rounded-lg border bg-indigo-500/10 border-indigo-500/30">
              <div className="flex items-center gap-2 text-sm text-indigo-300">
                <Bot className="w-4 h-4" />
                <span>
                  Creating task for AI Officer: {officers.find(o => o.user_email === taskForm.assign_to_email)?.callsign}
                </span>
              </div>
            </div>
          )}
          {taskForm.assign_to_email && taskForm.is_tentative && (
            <div className="p-3 rounded-lg border bg-amber-500/10 border-amber-500/30">
              <div className="flex items-center gap-2 text-sm text-amber-300">
                <AlertTriangle className="w-4 h-4" />
                <span>
                  Creating tentative task for {officers.find(o => o.user_email === taskForm.assign_to_email)?.callsign} (requires their acceptance)
                </span>
              </div>
            </div>
          )}

          {/* ── Key Result Focus ── */}
          {selectedActivity?.key_results && selectedActivity.key_results.length > 0 && (
            <div className="min-w-0">
              <label className="text-sm text-slate-400 mb-2 block">Key Result Focus</label>
              <Select value={taskForm.key_result_focus} onValueChange={(v) => setTaskForm({ ...taskForm, key_result_focus: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-700 max-w-full [&>span]:truncate [&>span]:max-w-[calc(100%-24px)]">
                  <SelectValue placeholder="Select key result to advance..." />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-32px)]">
                  {selectedActivity.key_results.filter(kr => kr.description).map((kr, idx) => (
                    <SelectItem key={idx} value={kr.description} className="max-w-full">
                      <span className="line-clamp-2">{kr.description} ({kr.current_value}/{kr.target} {kr.unit})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ── Task Description + AI Generate ── */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Task Description</label>
            <Textarea data-testid="tasks-addedittaskdialog-textarea"
              value={taskForm.task_description}
              onChange={(e) => setTaskForm({ ...taskForm, task_description: e.target.value })}
              placeholder="What will you accomplish today?"
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[100px] mb-2"
            />
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                onClick={async () => {
                  if (!taskForm.activity_id) {
                    alert("Please select an activity first.");
                    return;
                  }
                  setIsGeneratingTask(true);
                  setAiAssessment(null);
                  try {
                    const res = await base44.functions.invoke('generateTasks', { activity_id: taskForm.activity_id, mode: 'generate', target: 'human' });
                    if (res.data && res.data.suggested_task) {
                      setTaskForm(prev => ({ ...prev, task_description: res.data.suggested_task }));
                      setAiAssessment(res.data.assessment);
                    }
                  } catch (err) {
                    console.error("Failed to generate task", err);
                    alert("Failed to generate task. Please try again.");
                  } finally {
                    setIsGeneratingTask(false);
                  }
                }}
                disabled={isGeneratingTask || !taskForm.activity_id}
                className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {isGeneratingTask ? 'Generating...' : 'AI Generate Task'}
              </Button>

              {aiAssessment && aiAssessment.type !== 'ok' && (
                <div className={`text-sm p-3 rounded-md flex items-start gap-2 ${
                  aiAssessment.type === 'redundant' ? 'bg-red-500/10 border border-red-500/30 text-red-200' :
                  'bg-yellow-500/10 border border-yellow-500/30 text-yellow-200'
                }`}>
                  {aiAssessment.type === 'redundant' ?
                    <AlertOctagon className="w-4 h-4 mt-0.5 shrink-0 text-red-400" /> :
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-yellow-400" />
                  }
                  <div className="flex-1">
                    <span className="font-semibold block mb-1">
                      {aiAssessment.type === 'redundant' ? 'Redundant Task Detected' : 'Potential Overlap Detected'}
                    </span>
                    {aiAssessment.message}
                    {aiAssessment.linked_task_id && (
                      <div className="mt-1">
                        <span className="text-xs opacity-70">Refers to existing task.</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Gamification Points Panel ── */}
          <TaskCategorizationPanel
            taskDescription={taskForm.task_description}
            keyResultFocus={taskForm.key_result_focus}
            activityId={taskForm.activity_id}
            onCategorize={(gamificationData) => {
              setTaskForm(prev => ({ ...prev, ...gamificationData }));
            }}
            initialValues={taskForm.category ? {
              category: taskForm.category,
              xp_value: taskForm.xp_value,
              ep_value: taskForm.ep_value,
              skill_rewards: taskForm.skill_rewards,
            } : null}
          />

          {/* ── Tags ── */}
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Tags</label>
            <div className="flex flex-wrap gap-2 p-3 bg-slate-950 border border-slate-700 rounded-lg min-h-[42px] mb-2">
              {taskForm.tags.map((tag, idx) => {
                const match = tag.match(/\(([^)]+)\)$/);
                const tagType = match ? match[1] : '';
                return (
                  <Badge
                    key={idx}
                    variant="outline"
                    className={`text-xs ${
                      tagType === 'Officer' ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30' :
                      tagType === 'Artifact' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' :
                      tagType === 'Ritual' ? 'bg-purple-500/10 text-purple-400 border-purple-500/30' :
                      tagType === 'Policy' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' :
                      'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                    }`}
                  >
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="ml-1 hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={currentTagType} onValueChange={setCurrentTagType}>
                <SelectTrigger className="bg-slate-950 border-slate-700 w-28 sm:w-32 flex-shrink-0">
                  <SelectValue placeholder="Type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Officer">Officer</SelectItem>
                  <SelectItem value="Artifact">Artifact</SelectItem>
                  <SelectItem value="Ritual">Ritual</SelectItem>
                  <SelectItem value="Policy">Policy</SelectItem>
                  <SelectItem value="Capability">Capability</SelectItem>
                </SelectContent>
              </Select>

              {currentTagType === 'Officer' ? (
                <SearchableTagSelect
                  options={officers.map(o => ({ value: o.user_email, label: o.callsign }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select officer..."
                  emptyMessage="No officers found."
                  className="flex-1"
                />
              ) : currentTagType === 'Artifact' ? (
                <SearchableTagSelect
                  options={artifacts.filter(a => {
                    if (a.is_company_secret) {
                      if (a.owner_email === user?.email) return true;
                      if ((a.editors || []).includes(user?.email)) return true;
                      if ((a.viewers || []).includes(user?.email)) return true;
                      return false;
                    }
                    return true;
                  }).map(a => ({ value: a.name, label: a.name }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select artifact..."
                  emptyMessage="No artifacts found."
                  className="flex-1"
                />
              ) : currentTagType === 'Ritual' ? (
                <SearchableTagSelect
                  options={rituals.filter(r => {
                    if (r.is_company_secret) {
                      if (r.owner_email === user?.email) return true;
                      if ((r.participants || []).includes(user?.email)) return true;
                      return false;
                    }
                    return true;
                  }).map(r => ({ value: r.name, label: r.name }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select ritual..."
                  emptyMessage="No rituals found."
                  className="flex-1"
                />
              ) : currentTagType === 'Policy' ? (
                <SearchableTagSelect
                  options={policies.map(p => ({ value: p.title, label: p.title }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select policy..."
                  emptyMessage="No policies found."
                  className="flex-1"
                />
              ) : currentTagType === 'Capability' ? (
                <SearchableTagSelect
                  options={capabilities.map(c => ({ value: c.name, label: c.name }))}
                  value={currentTagValue}
                  onValueChange={setCurrentTagValue}
                  placeholder="Select capability..."
                  emptyMessage="No capabilities found."
                  className="flex-1"
                />
              ) : currentTagType ? (
                <Input data-testid="tasks-addedittaskdialog-input-2"
                  placeholder={`Enter ${currentTagType.toLowerCase()} name...`}
                  value={currentTagValue}
                  onChange={(e) => setCurrentTagValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 flex-1"
                />
              ) : (
                <Input data-testid="tasks-addedittaskdialog-input-3"
                  placeholder="Select type first..."
                  disabled
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 flex-1"
                />
              )}

              <Button
                type="button"
                onClick={addTag}
                disabled={!currentTagType || !currentTagValue}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* ── Submit Artifact (edit mode only) ── */}
          {editingTask && (
            <div className="pt-4 border-t border-slate-700">
              <div className="text-xs text-slate-400 mb-2">Submit Artifact</div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input data-testid="tasks-addedittaskdialog-input-4"
                    type="file"
                    id="shared-artifact-upload"
                    className="hidden"
                    onChange={(e) => handleArtifactUpload(e, editingTask)}
                    disabled={uploadingArtifact}
                  />
                  <label
                    htmlFor="shared-artifact-upload"
                    className={`flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm cursor-pointer transition-colors ${uploadingArtifact ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploadingArtifact ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4" /> Upload & Submit for Approval</>
                    )}
                  </label>
                </div>
                <p className="text-xs text-slate-500">
                  Uploads a file and creates a pending artifact linked to this task.
                </p>
              </div>
            </div>
          )}

          {/* ── Action Buttons ── */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-slate-700">
            <Button data-testid="tasks-cancel-btn" variant="outline" onClick={() => { resetForm(); onClose(); }} className="text-slate-900 w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700 text-white w-full sm:w-auto">
              {editingTask ? 'Update Task' : 'Add Task'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </ConfirmableDialog>
  );
}